import { cn } from '@/lib/utils';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type Active,
  type Announcements,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragOverEvent,
  type DragStartEvent,
  type KeyboardCoordinateGetter,
  type Over,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { getEventCoordinates, isTouchEvent } from '@dnd-kit/utilities';
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  dropTarget,
  emptyZone,
  isZone,
  keyboardAfter,
  SECTION_NAMES,
  sidebarCollisions,
  SidebarDragContext,
  zoneOf,
  type DragData,
  type DragOver,
  type DropTarget,
} from './sidebar-dnd.util';
import type { SidebarRef } from './sidebar-layout.util';
import { openItemMenu, useSidebarPins } from './sidebar-menu.util';
import { PinIcon } from './pin-icon.component';
import { LIFTED_CARD } from './tabs-sidebar.styles';
import type { TabsNavItem } from './use-tabs-nav.hook';

/** Once picked up, Space or Enter drops the item and Escape puts it back. */
const KEYBOARD_CODES = {
  start: [] as string[],
  cancel: ['Escape'],
  end: ['Space', 'Enter'],
};

/**
 * How far a finger may stray from where it went down: while it holds still
 * to pick an item up, and after, for letting go to open the item's menu.
 */
const TOUCH_TOLERANCE = 8;

/** The arrows that, with Alt, pick an item up. */
const PICK_UP_CODES = ['ArrowUp', 'ArrowDown'];

/**
 * Picks an item up with Alt (Option on a Mac) and the up or down arrow, keys
 * no role uses: Space still opens a chat tab, as it does every button, and
 * Enter still opens links. The arrows move it from there.
 */
class SidebarKeyboardSensor extends KeyboardSensor {
  static activators: typeof KeyboardSensor.activators = [
    {
      eventName: 'onKeyDown',
      handler: (event, options, context) => {
        const { altKey, code } = event.nativeEvent as KeyboardEvent;
        if (!altKey || !PICK_UP_CODES.includes(code)) return false;
        return KeyboardSensor.activators[0].handler(
          event,
          { ...options, keyboardCodes: { ...KEYBOARD_CODES, start: [code] } },
          context
        );
      },
    },
  ];
}

const dataOf = (entry: Active | Over | null | undefined) =>
  entry?.data.current as Partial<DragData> | undefined;

function overOf(over: Over | null): DragOver | null {
  const data = dataOf(over);
  if (!data?.zone) return null;
  return { zone: data.zone, key: data.key ?? null };
}

/**
 * A finger held on an item would open its menu after a while, but the drag
 * picks the item up first: the menu stays shut while the item is lifted,
 * and opens if the finger lets go without moving it (see onDragEnd). Radix
 * clears its long press on a pointercancel from the item.
 */
function cancelLongPress(target: EventTarget | null) {
  if (!target) return;
  const cancel = new Event('pointercancel', { bubbles: true });
  Object.defineProperty(cancel, 'pointerType', { value: 'touch' });
  target.dispatchEvent(cancel);
}

/**
 * Once a click would follow the drop (the pointer comes up where it went
 * down), it would open the item it lands on: swallow it.
 */
function swallowNextClick() {
  const stop = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };
  window.addEventListener('click', stop, { capture: true, once: true });
  window.setTimeout(
    () => window.removeEventListener('click', stop, { capture: true }),
    0
  );
}

function sameTarget(a: DropTarget | null, b: DropTarget | null): boolean {
  return (
    a === b || (!!a && !!b && a.section === b.section && a.before === b.before)
  );
}

/**
 * Drag and drop for the tabbed sidebar, on dnd-kit: reorder the tiles and
 * the rows, move a pin between them, and drag a tab in to pin it. Nothing
 * shifts while dragging: the item lifts, and a line shows where it lands.
 */
export function SidebarDnd({
  favorites,
  pinned,
  children,
}: {
  favorites: readonly TabsNavItem[];
  pinned: readonly TabsNavItem[];
  children: ReactNode;
}) {
  const pins = useSidebarPins();
  const [active, setActive] = useState<DragData | null>(null);
  const [target, setTarget] = useState<DropTarget | null>(null);
  const targetRef = useRef<DropTarget | null>(null);
  const byKeyboard = useRef(false);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  // Whether the item has strayed from the finger's first point since it
  // was picked up.
  const strayed = useRef(false);

  const sections = useMemo(
    () => ({
      favorites: favorites.map((item) => item.key),
      pinned: pinned.map((item) => item.key),
    }),
    [favorites, pinned]
  );
  const refs = useMemo(
    () =>
      new Map<string, SidebarRef>(
        [...favorites, ...pinned].map((item) => [item.key, item.ref])
      ),
    [favorites, pinned]
  );
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const labels = useMemo(
    () =>
      new Map([...favorites, ...pinned].map((item) => [item.key, item.label])),
    [favorites, pinned]
  );
  // Where the last drag landed, for its announcement after it ends.
  const landed = useRef<DropTarget | null>(null);

  const collisionDetection = useCallback<CollisionDetection>(
    (args) => sidebarCollisions(args, sectionsRef.current),
    []
  );

  // By keyboard, an item moves from pin to pin: tabs are not stops, and a
  // section is one only while it is empty.
  const coordinateGetter = useCallback<KeyboardCoordinateGetter>(
    (event, args) => {
      const { context } = args;
      const containers = context.droppableContainers;
      const activeId = context.active?.id;
      const stops = {
        get: containers.get.bind(containers),
        getEnabled: () =>
          containers.getEnabled().filter((entry) => {
            if (entry.id === activeId) return true;
            if (zoneOf(entry) === 'tabs') return false;
            return (
              !isZone(entry.id) || emptyZone(entry.id, sectionsRef.current)
            );
          }),
      } as unknown as typeof containers;
      return sortableKeyboardCoordinates(event, {
        ...args,
        context: { ...context, droppableContainers: stops },
      });
    },
    []
  );

  const sensors = useSensors(
    // A mouse drags once it moves 4px. A finger holds still a quarter second
    // first, so a swipe that starts on a pin still scrolls the sidebar; once
    // a drag starts, the sensor keeps the page from scrolling.
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: TOUCH_TOLERANCE },
    }),
    useSensor(SidebarKeyboardSensor, {
      coordinateGetter,
      keyboardCodes: KEYBOARD_CODES,
    })
  );

  const track = (event: DragMoveEvent | DragOverEvent) => {
    const data = dataOf(event.active) as DragData | undefined;
    if (!data) return;
    if (Math.hypot(event.delta.x, event.delta.y) > TOUCH_TOLERANCE)
      strayed.current = true;
    const over = overOf(event.over);
    let after = false;
    if (over && over.key !== null && event.over) {
      if (byKeyboard.current) after = keyboardAfter(sections, data, over);
      else if (pointerStart.current) {
        const { rect } = event.over;
        const across = over.zone === 'favorites';
        after = across
          ? pointerStart.current.x + event.delta.x > rect.left + rect.width / 2
          : pointerStart.current.y + event.delta.y > rect.top + rect.height / 2;
      }
    }
    const next = dropTarget(sections, data, over, after);
    if (sameTarget(targetRef.current, next)) return;
    targetRef.current = next;
    setTarget(next);
  };

  const finish = () => {
    targetRef.current = null;
    pointerStart.current = null;
    setTarget(null);
    setActive(null);
  };

  const onDragStart = ({ active: started, activatorEvent }: DragStartEvent) => {
    const data = dataOf(started) as DragData | undefined;
    if (!data) return;
    // A mouse or a finger has a point; a key has none.
    const point = getEventCoordinates(activatorEvent);
    byKeyboard.current = point === null;
    pointerStart.current = point;
    strayed.current = false;
    if (isTouchEvent(activatorEvent)) cancelLongPress(activatorEvent.target);
    setActive(data);
  };

  const onDragEnd = ({
    active: ended,
    activatorEvent,
    delta,
  }: DragEndEvent) => {
    const data = dataOf(ended) as DragData | undefined;
    const landing = targetRef.current;
    // A finger that picked the item up and let it go where it was, without
    // moving it, opens its menu: the long press the drag took over.
    const start = pointerStart.current;
    const letGoInPlace =
      start !== null &&
      landing === null &&
      !strayed.current &&
      isTouchEvent(activatorEvent);
    const menuTarget = letGoInPlace ? activatorEvent.target : null;
    landed.current = pins && data ? landing : null;
    if (!byKeyboard.current) swallowNextClick();
    if (pins && data && landing) {
      const before = landing.before ? (refs.get(landing.before) ?? null) : null;
      // Into another section the item remounts under a new id, which dnd-kit
      // cannot give focus back to: follow it to where it lands, once
      // dnd-kit's own restore has had its frame.
      const remounts = byKeyboard.current && landing.section !== data.zone;
      if (remounts) pins.holdFocus(data.key);
      pins.layout.place(data.ref, landing.section, before);
      if (remounts) requestAnimationFrame(() => pins.restoreFocus());
    }
    finish();
    if (start && menuTarget)
      openItemMenu(menuTarget, { x: start.x + delta.x, y: start.y + delta.y });
  };

  const labelOf = (entry: Active | Over | null) => dataOf(entry)?.label;
  // Where a drop lands, in words: dnd-kit runs onDragOver, which tracks the
  // landing, before it announces, so the landing is current here.
  const placeOf = (landing: DropTarget, end: string) => {
    const section = SECTION_NAMES[landing.section];
    const before = landing.before ? labels.get(landing.before) : undefined;
    return before ? `before ${before} in ${section}` : `${end} ${section}`;
  };
  const announcements: Announcements = {
    onDragStart: ({ active: started }) => `Picked up ${labelOf(started)}.`,
    onDragOver: ({ active: moving, over }) => {
      const landing = targetRef.current;
      if (landing)
        return `${labelOf(moving)} will go ${placeOf(landing, 'at the end of')}.`;
      const zone = dataOf(over)?.zone;
      return zone && zone !== 'tabs'
        ? `${labelOf(moving)} will stay where it is.`
        : `${labelOf(moving)} is not over a place to drop.`;
    },
    onDragEnd: ({ active: ended }) => {
      const landing = landed.current;
      return landing
        ? `${labelOf(ended)} moved ${placeOf(landing, 'to the end of')}.`
        : `${labelOf(ended)} was not moved.`;
    },
    onDragCancel: ({ active: cancelled }) =>
      `Moving ${labelOf(cancelled)} was cancelled.`,
  };

  const dragState = useMemo(
    () => ({ activeKey: active?.key ?? null, target }),
    [active, target]
  );

  if (!pins) return <>{children}</>;
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      accessibility={{
        announcements,
        screenReaderInstructions: {
          draggable:
            'To pick up an item, hold Alt, or Option on a Mac, and press the up or down arrow. Use the arrow keys to move it, then press space or enter to drop it, or escape to cancel.',
        },
      }}
      onDragStart={onDragStart}
      onDragMove={track}
      onDragOver={track}
      onDragEnd={onDragEnd}
      onDragCancel={finish}
    >
      <SidebarDragContext.Provider value={dragState}>
        {children}
      </SidebarDragContext.Provider>
      {createPortal(
        <DragOverlay dropAnimation={null}>
          {active ? <LiftedItem data={active} /> : null}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
}

/** The dragged item, lifted off the sidebar under the pointer. */
function LiftedItem({ data }: { data: DragData }) {
  const { item, preview } = data;
  if (preview === 'tile')
    return (
      <div
        className={cn(
          'flex h-full w-full flex-col items-center rounded-xl px-1 pt-[9px] text-center text-xs leading-[13px] text-foreground',
          LIFTED_CARD
        )}
      >
        <span className='grid h-[22px] place-items-center'>
          <PinIcon item={item} size={item.agentId || item.chat ? 22 : 20} />
        </span>
        <span className='mt-[3px] flex h-[26px] w-full items-center justify-center'>
          <span className='line-clamp-2 break-words'>{item.label}</span>
        </span>
      </div>
    );
  return (
    <div
      className={cn(
        'flex h-full w-full items-center gap-2 rounded-[10px] pr-2.5 pl-2 text-sm text-foreground',
        LIFTED_CARD
      )}
    >
      <span className='grid w-5 shrink-0 place-items-center'>
        <PinIcon item={item} size={item.chat ? 20 : 16} />
      </span>
      <span className='min-w-0 flex-1 truncate'>{item.label}</span>
    </div>
  );
}

/**
 * Where a drop lands, as Arc draws it: a blue line with a ring at its start,
 * across a row's top or bottom, or down a tile's side.
 */
export function DropLine({
  position,
  across,
}: {
  position: 'before' | 'after';
  /** Down the side of a tile in the grid, rather than across a row. */
  across: boolean;
}) {
  if (across)
    return (
      <span
        aria-hidden
        data-drop-line
        className={cn(
          'pointer-events-none absolute top-1 bottom-1 z-10 w-0.5 rounded-full bg-primary',
          position === 'before' ? '-left-[5px]' : '-right-[5px]'
        )}
      >
        <span className='absolute -top-1.5 -left-[3px] h-2 w-2 rounded-full border-2 border-primary bg-background' />
      </span>
    );
  return (
    <span
      aria-hidden
      data-drop-line
      className={cn(
        'pointer-events-none absolute right-1 left-1.5 z-10 h-0.5 rounded-full bg-primary',
        position === 'before' ? '-top-px' : '-bottom-px'
      )}
    >
      <span className='absolute -top-[3px] -left-1.5 h-2 w-2 rounded-full border-2 border-primary bg-background' />
    </span>
  );
}
